import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Circle } from 'lucide-react';
import { fetchPublishDetail, postInputReady } from '@/lib/api/publish';
import { toast } from '@/stores/toast';
import { ApiClientError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { useEffectivePermissions } from '@/hooks/useEffectivePermissions';

type Props = {
  nodeId: string;
  targetMonth: string; // 'YYYY-MM'
  dataType: string;
};

/**
 * v0.3 (S-8): 「入力完了とする」ボタン。Sales/DeliveryDetails/ManualEntry の4画面に差し込む。
 * feature flag OFF・対象外データ種別・store/month未選択・権限なしのいずれかでは非表示。
 */
export const InputReadyButton = ({ nodeId, targetMonth, dataType }: Props) => {
  const qc = useQueryClient();
  const tenantFeatures = useAuthStore((s) => s.user?.tenant_features);
  const { hasPermissionForFeatureAction } = useEffectivePermissions();
  const enabled = tenantFeatures?.publish_gate_enabled === true && !!nodeId && !!targetMonth && /^\d{4}-\d{2}$/.test(targetMonth);

  const { data } = useQuery({
    queryKey: ['publish-detail', nodeId, targetMonth],
    queryFn: () => fetchPublishDetail(nodeId, targetMonth),
    enabled,
  });

  const current = data?.input_status.find((s) => s.dataType === dataType);
  const isReady = current?.inputReady === 1;

  const mut = useMutation({
    mutationFn: (nextReady: boolean) => postInputReady({ node_id: nodeId, target_month: targetMonth, data_type: dataType, input_ready: nextReady }),
    onSuccess: (_res, nextReady) => {
      qc.invalidateQueries({ queryKey: ['publish-detail', nodeId, targetMonth] });
      qc.invalidateQueries({ queryKey: ['publish-status'] });
      toast.success(nextReady ? '入力完了にしました' : '入力完了を取消しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  if (!enabled) return null;
  // バックエンド (publish.ts POST /input-ready) は import:manage のみを認可条件とするため、
  // フロントの表示判定もこれに合わせる (見えるのに403になる不整合を避ける)
  if (!hasPermissionForFeatureAction('import', 'manage')) return null;

  return (
    <button
      type="button"
      onClick={() => mut.mutate(!isReady)}
      disabled={mut.isPending}
      className={`inline-flex items-center gap-1.5 h-10 px-4 rounded-md text-[13px] font-extrabold btn-press-secondary focus-ring disabled:opacity-45 min-h-[44px] sm:min-h-[40px] ${
        isReady ? 'bg-success-bg text-success-text border border-success-border' : 'bg-white text-text-body border border-surface-border hover:bg-surface-sunken'
      }`}
    >
      {isReady ? <CheckCircle2 size={16} /> : <Circle size={16} />}
      {mut.isPending ? '処理中…' : isReady ? '入力完了を取消' : '入力完了とする'}
    </button>
  );
};
